import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, Download, Package, Receipt, Ship, Plane, Calendar, FileText, TrendingUp, Filter, Eye, Printer, ArrowLeft, X } from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";

export default function Reports() {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(false);
  const [batches, setBatches] = useState([]);
  const [stats, setStats] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  
  // Años disponibles basados en fecha_tentativa_embarque
  const [availableYears, setAvailableYears] = useState([]);
  const [selectedYears, setSelectedYears] = useState([]);
  
  // Control de permisos
  const isAdmin = hasRole("admin");
  const isProveedor = hasRole("proveedor");
  const isCliente = hasRole("cliente");
  
  // Vista de detalle
  const [detailView, setDetailView] = useState(null); // 'compras', 'ventas', 'lotes', 'kilos', 'maritimos', 'aereos'
  
  // Filtros
  const [filters, setFilters] = useState({
    fechaDesde: "",
    fechaHasta: "",
    numeroLote: "",
    contenedor: "",
    awb: "",
    bl: "",
    estado: "all",
    proveedor: "all",
    cliente: "all",
    producto: "all"
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [batchesRes, statsRes, suppliersRes, clientsRes, productsRes] = await Promise.all([
        api.get("/batches"),
        api.get("/stats/dashboard"),
        api.get("/suppliers"),
        api.get("/clients"),
        api.get("/products")
      ]);
      
      setBatches(batchesRes.data);
      setStats(statsRes.data);
      setSuppliers(suppliersRes.data);
      setClients(clientsRes.data);
      setProducts(productsRes.data);
      
      // Extraer años únicos de fecha_tentativa_embarque
      const years = new Set();
      batchesRes.data.forEach(batch => {
        if (batch.fecha_tentativa_embarque) {
          const year = batch.fecha_tentativa_embarque.substring(0, 4);
          if (year && !isNaN(parseInt(year))) {
            years.add(parseInt(year));
          }
        }
      });
      const sortedYears = Array.from(years).sort((a, b) => b - a); // Descendente
      setAvailableYears(sortedYears);
      setSelectedYears(sortedYears); // Todos seleccionados por defecto
      
    } catch (error) {
      toast.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  const handleYearToggle = (year) => {
    setSelectedYears(prev => 
      prev.includes(year) 
        ? prev.filter(y => y !== year)
        : [...prev, year]
    );
  };

  const selectAllYears = () => {
    if (selectedYears.length === availableYears.length) {
      setSelectedYears([]);
    } else {
      setSelectedYears([...availableYears]);
    }
  };

  const applyFilters = (data = batches) => {
    let filtered = [...data];
    
    // Filtrar por años seleccionados (basado en fecha_tentativa_embarque)
    if (selectedYears.length > 0 && selectedYears.length < availableYears.length) {
      filtered = filtered.filter(b => {
        if (!b.fecha_tentativa_embarque) return false;
        const year = parseInt(b.fecha_tentativa_embarque.substring(0, 4));
        return selectedYears.includes(year);
      });
    }
    
    if (filters.fechaDesde) {
      filtered = filtered.filter(b => b.created_at >= filters.fechaDesde);
    }
    if (filters.fechaHasta) {
      filtered = filtered.filter(b => b.created_at <= filters.fechaHasta + "T23:59:59");
    }
    if (filters.numeroLote) {
      filtered = filtered.filter(b => 
        b.numero_lote?.toLowerCase().includes(filters.numeroLote.toLowerCase())
      );
    }
    if (filters.contenedor) {
      filtered = filtered.filter(b => 
        b.contenedor?.toLowerCase().includes(filters.contenedor.toLowerCase())
      );
    }
    if (filters.awb) {
      filtered = filtered.filter(b => 
        b.awb_aereo?.toLowerCase().includes(filters.awb.toLowerCase())
      );
    }
    if (filters.bl) {
      filtered = filtered.filter(b => 
        b.bl_maritimo?.toLowerCase().includes(filters.bl.toLowerCase())
      );
    }
    if (filters.estado && filters.estado !== "all") {
      filtered = filtered.filter(b => b.estado === filters.estado);
    }
    if (filters.proveedor && filters.proveedor !== "all") {
      filtered = filtered.filter(b => b.proveedor_id === filters.proveedor);
    }
    if (filters.cliente && filters.cliente !== "all") {
      filtered = filtered.filter(b => b.cliente_id === filters.cliente);
    }
    if (filters.producto && filters.producto !== "all") {
      filtered = filtered.filter(b => b.producto_id === filters.producto);
    }
    
    // Filtros especiales según la vista de detalle
    if (detailView === 'maritimos') {
      filtered = filtered.filter(b => b.contenedor);
    } else if (detailView === 'aereos') {
      filtered = filtered.filter(b => b.awb_aereo);
    }
    
    return filtered;
  };

  const clearFilters = () => {
    setFilters({
      fechaDesde: "",
      fechaHasta: "",
      numeroLote: "",
      contenedor: "",
      awb: "",
      bl: "",
      estado: "all",
      proveedor: "all",
      cliente: "all",
      producto: "all"
    });
  };

  const formatNumber = (num, decimals = 2) => {
    if (num === null || num === undefined) return "0";
    return Number(num).toLocaleString("es-ES", {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  };

  // Formatear fecha SIN conversión de zona horaria
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const datePart = dateStr.split('T')[0];
    const parts = datePart.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    return dateStr;
  };

  const formatCurrency = (num) => {
    if (num === null || num === undefined) return "0,00 €";
    return Number(num).toLocaleString("es-ES", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + " €";
  };

  const filteredBatches = applyFilters();

  // Calcular estadísticas de los lotes filtrados
  const calcStats = () => {
    const totalKilosNetos = filteredBatches.reduce((sum, b) => sum + (b.peso_neto || 0), 0);
    const totalKilosBrutos = filteredBatches.reduce((sum, b) => sum + (b.peso_bruto || 0), 0);
    const totalBultos = filteredBatches.reduce((sum, b) => sum + (b.bultos || 0), 0);
    const totalPalets = filteredBatches.reduce((sum, b) => sum + (b.palets || 0), 0);
    
    const conContenedor = filteredBatches.filter(b => b.contenedor).length;
    const conAWB = filteredBatches.filter(b => b.awb_aereo).length;
    
    // EXCLUIR RECHAZADOS de los cálculos económicos
    const lotesParaCalculos = filteredBatches.filter(b => b.estado !== "rechazado");
    
    // Importes totales (excluyendo rechazados)
    const importeCompras = lotesParaCalculos.reduce((sum, b) => 
      sum + ((b.precio_coste || 0) * (b.peso_neto || 0)), 0
    );
    const importeVentas = lotesParaCalculos.reduce((sum, b) => 
      sum + ((b.precio_venta || 0) * (b.peso_neto || 0)), 0
    );
    
    // Contadores de facturas (todos los lotes)
    const facturasOrigenEmitidas = filteredBatches.filter(b => b.factura_origen && b.factura_origen.trim() !== "").length;
    const facturasOrigenPendientes = filteredBatches.length - facturasOrigenEmitidas;
    
    const facturasClienteEmitidas = filteredBatches.filter(b => b.factura_destino && b.factura_destino.trim() !== "").length;
    const facturasClientePendientes = filteredBatches.length - facturasClienteEmitidas;
    
    // Lotes CON factura cliente (ya facturados) - excluyendo rechazados
    const lotesConFacturaCliente = lotesParaCalculos.filter(b => b.factura_destino && b.factura_destino.trim() !== "");
    const importeVentasFacturadas = lotesConFacturaCliente.reduce((sum, b) => 
      sum + ((b.precio_venta || 0) * (b.peso_neto || 0)), 0
    );
    const importeComprasFacturadas = lotesConFacturaCliente.reduce((sum, b) => 
      sum + ((b.precio_coste || 0) * (b.peso_neto || 0)), 0
    );
    const margen = importeVentasFacturadas - importeComprasFacturadas; // Margen de lo YA facturado
    
    // Lotes SIN factura cliente (pendientes de facturar) - excluyendo rechazados
    const lotesSinFacturaCliente = lotesParaCalculos.filter(b => !b.factura_destino || b.factura_destino.trim() === "");
    const kilosPendientesFacturar = lotesSinFacturaCliente.reduce((sum, b) => sum + (b.peso_neto || 0), 0);
    const previstoFacturar = lotesSinFacturaCliente.reduce((sum, b) => 
      sum + ((b.precio_venta || 0) * (b.peso_neto || 0)), 0
    );
    
    // Margen total = margen de todos los lotes (facturados + pendientes) - excluyendo rechazados
    const margenTotalConPrevisto = importeVentas - importeCompras;
    
    return {
      totalLotes: filteredBatches.length,
      totalKilosNetos,
      totalKilosBrutos,
      totalBultos,
      totalPalets,
      conContenedor,
      conAWB,
      importeCompras,
      importeVentas,
      margen,
      // Nuevos campos
      facturasOrigenEmitidas,
      facturasOrigenPendientes,
      facturasClienteEmitidas,
      facturasClientePendientes,
      kilosPendientesFacturar,
      previstoFacturar,
      margenTotalConPrevisto
    };
  };

  const calculatedStats = calcStats();

  // Abrir vista de detalle al hacer clic en un KPI
  const openDetailView = (view) => {
    setDetailView(view);
  };

  const handlePrint = () => {
    window.print();
  };

  // Vista de detalle
  const renderDetailView = () => {
    const titleMap = {
      'compras': 'Detalle de Compras (Facturas de Origen)',
      'ventas': 'Detalle de Ventas (Facturas a Cliente)',
      'lotes': 'Detalle de Lotes',
      'kilos': 'Detalle de Kilos',
      'maritimos': 'Detalle de Envíos Marítimos',
      'aereos': 'Detalle de Envíos Aéreos'
    };

    return (
      <div className="detail-view-page">
        {/* Header NO imprimible */}
        <div className="no-print flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => setDetailView(null)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Volver
            </Button>
            <h2 className="text-xl font-bold">{titleMap[detailView]}</h2>
          </div>
          <Button onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Imprimir Informe
          </Button>
        </div>

        {/* Filtros */}
        <Card className="mb-4 no-print">
          <CardContent className="pt-4">
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Fecha Desde</Label>
                <Input
                  type="date"
                  value={filters.fechaDesde}
                  onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Fecha Hasta</Label>
                <Input
                  type="date"
                  value={filters.fechaHasta}
                  onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })}
                />
              </div>
              {/* Filtro Proveedor: solo admin y proveedor, NO clientes */}
              {!isCliente && (
                <div className="space-y-1">
                  <Label className="text-xs">Proveedor</Label>
                  <Select value={filters.proveedor} onValueChange={(v) => setFilters({ ...filters, proveedor: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {suppliers.map(s => (
                        <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {/* Filtro Cliente: NO visible para proveedores */}
              {!isProveedor && (
                <div className="space-y-1">
                  <Label className="text-xs">Cliente</Label>
                  <Select value={filters.cliente} onValueChange={(v) => setFilters({ ...filters, cliente: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs">Producto</Label>
                <Select value={filters.producto} onValueChange={(v) => setFilters({ ...filters, producto: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    {products.map(p => (
                      <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Estado</Label>
                <Select value={filters.estado} onValueChange={(v) => setFilters({ ...filters, estado: v })}>
                  <SelectTrigger>
                    <SelectValue placeholder="Todos" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="documentacion_inicial">Documentación inicial</SelectItem>
                    <SelectItem value="en_planta">En la planta</SelectItem>
                    <SelectItem value="en_reconocimiento">En reconocimiento</SelectItem>
                    <SelectItem value="listo_para_cargar">Listo para cargar</SelectItem>
                    <SelectItem value="en_transito">En tránsito</SelectItem>
                    <SelectItem value="en_destino">En destino</SelectItem>
                    <SelectItem value="entregado_cliente">Entregado a cliente</SelectItem>
                    <SelectItem value="completado">Completado</SelectItem>
                    <SelectItem value="anulado">Anulado</SelectItem>
                    <SelectItem value="rechazado">Rechazado</SelectItem>
                    <SelectItem value="otros">Otros</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex justify-end mt-3">
              <Button variant="outline" size="sm" onClick={clearFilters}>
                <X className="w-3 h-3 mr-1" />
                Limpiar Filtros
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Contenido imprimible */}
        <div className="print-content bg-white p-4 rounded-lg shadow">
          {/* Encabezado del informe */}
          <div className="print-header hidden print:block mb-4 pb-4 border-b">
            <h1 className="text-xl font-bold">{titleMap[detailView]}</h1>
            <p className="text-sm text-slate-500">
              Generado: {new Date().toLocaleDateString("es-ES")} | 
              {filters.fechaDesde && ` Desde: ${filters.fechaDesde}`}
              {filters.fechaHasta && ` Hasta: ${filters.fechaHasta}`}
            </p>
          </div>

          {/* Resumen */}
          <div className="grid grid-cols-4 gap-4 mb-4 print:grid-cols-4">
            <div className="bg-slate-50 p-3 rounded text-center">
              <p className="text-2xl font-bold">{filteredBatches.length}</p>
              <p className="text-xs text-slate-500">Lotes</p>
            </div>
            <div className="bg-slate-50 p-3 rounded text-center">
              <p className="text-2xl font-bold">{formatNumber(calculatedStats.totalKilosNetos, 0)}</p>
              <p className="text-xs text-slate-500">Kg Netos</p>
            </div>
            {/* Compras: solo admin y proveedor, NO clientes */}
            {!isCliente && (
              <div className="bg-red-50 p-3 rounded text-center">
                <p className="text-2xl font-bold text-red-700">{formatCurrency(calculatedStats.importeCompras)}</p>
                <p className="text-xs text-red-500">Compras</p>
              </div>
            )}
            <div className="bg-green-50 p-3 rounded text-center">
              <p className="text-2xl font-bold text-green-700">{formatCurrency(calculatedStats.importeVentas)}</p>
              <p className="text-xs text-green-500">Ventas</p>
            </div>
          </div>

          {/* Tabla de datos */}
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-100">
                  <TableHead className="text-xs">Fecha</TableHead>
                  <TableHead className="text-xs">Lote</TableHead>
                  <TableHead className="text-xs">Producto</TableHead>
                  {/* Proveedor: solo admin y proveedor, NO clientes */}
                  {!isCliente && <TableHead className="text-xs">Proveedor</TableHead>}
                  {/* Cliente: NO visible para proveedores */}
                  {!isProveedor && <TableHead className="text-xs">Cliente</TableHead>}
                  <TableHead className="text-xs">Contenedor/AWB</TableHead>
                  <TableHead className="text-xs text-right">Kg Netos</TableHead>
                  {/* P.Coste: solo admin y proveedor, NO clientes */}
                  {!isCliente && <TableHead className="text-xs text-right">P.Coste</TableHead>}
                  {/* P.Venta: NO visible para proveedores */}
                  {!isProveedor && <TableHead className="text-xs text-right">P.Venta</TableHead>}
                  {/* Importe Compra: solo admin y proveedor, NO clientes */}
                  {!isCliente && <TableHead className="text-xs text-right">Importe Compra</TableHead>}
                  {/* Importe Venta: NO visible para proveedores */}
                  {!isProveedor && <TableHead className="text-xs text-right">Importe Venta</TableHead>}
                  <TableHead className="text-xs">Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredBatches.map((batch) => {
                  const importeCompra = (batch.precio_coste || 0) * (batch.peso_neto || 0);
                  const importeVenta = (batch.precio_venta || 0) * (batch.peso_neto || 0);
                  const proveedor = suppliers.find(s => s.id === batch.proveedor_id);
                  const cliente = clients.find(c => c.id === batch.cliente_id);
                  
                  return (
                    <TableRow key={batch.id} className="text-xs">
                      <TableCell>{formatDate(batch.created_at)}</TableCell>
                      <TableCell className="font-medium">{batch.numero_lote}</TableCell>
                      <TableCell>{batch.producto_nombre || "-"}</TableCell>
                      {/* Proveedor: solo admin y proveedor, NO clientes */}
                      {!isCliente && <TableCell>{proveedor?.nombre || "-"}</TableCell>}
                      {/* Cliente: NO visible para proveedores */}
                      {!isProveedor && <TableCell>{cliente?.nombre || "-"}</TableCell>}
                      <TableCell className="font-mono">
                        {batch.contenedor || batch.awb_aereo || batch.bl_maritimo || "-"}
                      </TableCell>
                      <TableCell className="text-right font-mono">{formatNumber(batch.peso_neto)}</TableCell>
                      {/* P.Coste: solo admin y proveedor, NO clientes - Convertido de USD a EUR */}
                      {!isCliente && <TableCell className="text-right font-mono">{formatNumber(batch.cotizacion_eur_usd ? (batch.precio_coste || 0) / batch.cotizacion_eur_usd : batch.precio_coste)} €</TableCell>}
                      {/* P.Venta: NO visible para proveedores */}
                      {!isProveedor && <TableCell className="text-right font-mono">{formatNumber(batch.precio_venta)} €</TableCell>}
                      {/* Importe Compra: solo admin y proveedor, NO clientes */}
                      {!isCliente && <TableCell className="text-right font-mono text-red-600">{formatNumber(importeCompra)} €</TableCell>}
                      {/* Importe Venta: NO visible para proveedores */}
                      {!isProveedor && <TableCell className="text-right font-mono text-green-600">{formatNumber(importeVenta)} €</TableCell>}
                      <TableCell>
                        <span className={`px-1 py-0.5 rounded text-[10px] ${
                          batch.estado === 'completado' ? 'bg-emerald-100 text-emerald-700' :
                          batch.estado === 'en_transito' ? 'bg-blue-100 text-blue-700' :
                          batch.estado === 'recibido' ? 'bg-green-100 text-green-700' :
                          batch.estado === 'anulado' ? 'bg-gray-100 text-gray-700' :
                          'bg-yellow-100 text-yellow-700'
                        }`}>
                          {batch.estado}
                        </span>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
              <tfoot>
                <tr className="bg-slate-800 text-white font-bold">
                  {/* Calcular colspan dinámicamente */}
                  <td colSpan={isProveedor ? 5 : (isCliente ? 5 : 6)} className="px-4 py-2 text-xs">TOTALES</td>
                  <td className="px-4 py-2 text-xs text-right font-mono">{formatNumber(calculatedStats.totalKilosNetos)}</td>
                  {/* P.Coste placeholder: solo admin y proveedor */}
                  {!isCliente && <td className="px-4 py-2 text-xs text-right">-</td>}
                  {/* P.Venta placeholder: NO visible para proveedores */}
                  {!isProveedor && <td className="px-4 py-2 text-xs text-right">-</td>}
                  {/* Importe Compra: solo admin y proveedor */}
                  {!isCliente && <td className="px-4 py-2 text-xs text-right font-mono">{formatCurrency(calculatedStats.importeCompras)}</td>}
                  {/* Importe Ventas: NO visible para proveedores */}
                  {!isProveedor && <td className="px-4 py-2 text-xs text-right font-mono">{formatCurrency(calculatedStats.importeVentas)}</td>}
                  <td></td>
                </tr>
              </tfoot>
            </Table>
          </div>
        </div>

        {/* Estilos para impresión horizontal */}
        <style>{`
          @media print {
            @page {
              size: A4 landscape;
              margin: 8mm;
            }
            
            * {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            
            .no-print,
            .print-hide,
            [data-testid="app-sidebar"],
            .sidebar,
            aside {
              display: none !important;
            }
            
            .detail-view-page {
              display: block !important;
            }
            
            .print-content {
              box-shadow: none !important;
            }
            
            .print-header {
              display: block !important;
            }
            
            table {
              font-size: 9px !important;
            }
            
            th, td {
              padding: 2px 4px !important;
            }
            
            .bg-slate-800 {
              background-color: #1e293b !important;
              color: white !important;
            }
            
            .bg-slate-100 {
              background-color: #f1f5f9 !important;
            }
            
            .bg-slate-50, .bg-red-50, .bg-green-50 {
              background-color: #f8fafc !important;
            }
          }
        `}</style>
      </div>
    );
  };

  // Si hay vista de detalle, mostrarla
  if (detailView) {
    return renderDetailView();
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Informes</h1>
          <p className="text-sm text-slate-500">Haz clic en cualquier KPI para ver el detalle</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Previsión de Llegada - Visible para todos */}
          <Button onClick={() => navigate("/reports/prevision-llegada")} variant="outline" className="border-sky-300 text-sky-700 hover:bg-sky-50">
            <Calendar className="w-4 h-4 mr-2" />
            Previsión Llegada
          </Button>
          
          {!isCliente && (
            <>
              <Button onClick={() => navigate("/reports/informe-resumen")} variant="outline" className="border-purple-300 text-purple-700 hover:bg-purple-50">
                <FileText className="w-4 h-4 mr-2" />
                Informe Resumen
              </Button>
              {!isProveedor && (
                <Button onClick={() => navigate("/reports/informe-lau")} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50">
                  <FileText className="w-4 h-4 mr-2" />
                  Seguimiento Lotes
                </Button>
              )}
            </>
          )}
          <Button onClick={() => navigate("/reports/seguimiento-cargas")} className="bg-blue-600 hover:bg-blue-700">
            <Ship className="w-4 h-4 mr-2" />
            Seguimiento de Cargas
          </Button>
          
          {/* Trazabilidad - Solo Admin y Admin Consultor */}
          {(hasRole("admin") || hasRole("admin_consultor")) && (
            <Button onClick={() => navigate("/reports/traceability")} className="bg-emerald-600 hover:bg-emerald-700">
              <FileText className="w-4 h-4 mr-2" />
              Trazabilidad
            </Button>
          )}
          
          {/* Informe de Descuentos - Solo Admin y Admin Consultor */}
          {(hasRole("admin") || hasRole("admin_consultor")) && (
            <Button onClick={() => navigate("/reports/discounts")} className="bg-amber-600 hover:bg-amber-700">
              <FileText className="w-4 h-4 mr-2" />
              Informe Descuentos
            </Button>
          )}
        </div>
      </div>

      {/* Filtro por años (basado en fecha_tentativa_embarque) */}
      {availableYears.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Filtrar por año de embarque:</span>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {availableYears.map((year) => (
                <div 
                  key={year}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-full cursor-pointer transition-all border ${
                    selectedYears.includes(year)
                      ? 'bg-blue-100 border-blue-300 text-blue-800'
                      : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                  }`}
                  onClick={() => handleYearToggle(year)}
                >
                  <Checkbox
                    checked={selectedYears.includes(year)}
                    onCheckedChange={() => handleYearToggle(year)}
                    className="h-4 w-4"
                  />
                  <span className="text-sm font-medium">{year}</span>
                </div>
              ))}
            </div>
            <button
              onClick={selectAllYears}
              className="text-xs text-blue-600 hover:text-blue-800 underline"
            >
              {selectedYears.length === availableYears.length ? "Deseleccionar todos" : "Seleccionar todos"}
            </button>
          </div>
        </div>
      )}

      <Tabs defaultValue="resumen" className="space-y-4">
        <TabsList>
          <TabsTrigger value="resumen">Resumen General</TabsTrigger>
          <TabsTrigger value="lotes">Consulta de Lotes</TabsTrigger>
          <TabsTrigger value="facturacion">Facturación</TabsTrigger>
        </TabsList>

        {/* TAB: Resumen General */}
        <TabsContent value="resumen" className="space-y-4">
          {/* KPIs - CLICKEABLES */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card 
              className="cursor-pointer hover:shadow-lg hover:border-sky-300 transition-all"
              onClick={() => openDetailView('lotes')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-sky-100 flex items-center justify-center">
                    <Package className="w-5 h-5 text-sky-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{stats?.total_batches || 0}</p>
                    <p className="text-xs text-slate-500">Total Lotes</p>
                  </div>
                </div>
                <p className="text-[10px] text-sky-600 mt-2">Clic para ver detalle →</p>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-lg hover:border-emerald-300 transition-all"
              onClick={() => openDetailView('kilos')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{formatNumber(calculatedStats.totalKilosNetos, 0)}</p>
                    <p className="text-xs text-slate-500">Kilos Netos Totales</p>
                  </div>
                </div>
                <p className="text-[10px] text-emerald-600 mt-2">Clic para ver detalle →</p>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-lg hover:border-blue-300 transition-all"
              onClick={() => openDetailView('maritimos')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                    <Ship className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{calculatedStats.conContenedor}</p>
                    <p className="text-xs text-slate-500">Envíos Marítimos</p>
                  </div>
                </div>
                <p className="text-[10px] text-blue-600 mt-2">Clic para ver detalle →</p>
              </CardContent>
            </Card>
            
            <Card 
              className="cursor-pointer hover:shadow-lg hover:border-purple-300 transition-all"
              onClick={() => openDetailView('aereos')}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Plane className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">{calculatedStats.conAWB}</p>
                    <p className="text-xs text-slate-500">Envíos Aéreos</p>
                  </div>
                </div>
                <p className="text-[10px] text-purple-600 mt-2">Clic para ver detalle →</p>
              </CardContent>
            </Card>
          </div>

          {/* Resumen de Facturación - CLICKEABLES - Filtrado por rol */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Total Compras: solo admin y proveedor */}
            {!isCliente && (
              <Card 
                className="bg-red-50 border-red-200 cursor-pointer hover:shadow-lg hover:border-red-400 transition-all"
                onClick={() => openDetailView('compras')}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-red-700">Total Compras (Origen)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-red-700">{formatCurrency(calculatedStats.importeCompras)}</p>
                  <p className="text-[10px] text-red-500 mt-2">Clic para ver detalle →</p>
                </CardContent>
              </Card>
            )}
            
            {/* Total Ventas: solo admin y cliente */}
            {!isProveedor && (
              <Card 
                className="bg-green-50 border-green-200 cursor-pointer hover:shadow-lg hover:border-green-400 transition-all"
                onClick={() => openDetailView('ventas')}
              >
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm text-green-700">Total Ventas (Cliente)</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-green-700">{formatCurrency(calculatedStats.importeVentas)}</p>
                  <p className="text-[10px] text-green-500 mt-2">Clic para ver detalle →</p>
                </CardContent>
              </Card>
            )}
            
            {/* Margen Bruto: solo admin */}
            {isAdmin && (
              <Card className={calculatedStats.margenTotalConPrevisto >= 0 ? "bg-emerald-50 border-emerald-200" : "bg-orange-50 border-orange-200"}>
                <CardHeader className="pb-2">
                  <CardTitle className={`text-sm ${calculatedStats.margenTotalConPrevisto >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                    Margen Bruto
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className={`text-2xl font-bold ${calculatedStats.margenTotalConPrevisto >= 0 ? 'text-emerald-700' : 'text-orange-700'}`}>
                    {formatCurrency(calculatedStats.margenTotalConPrevisto)}
                  </p>
                  <p className="text-[10px] text-slate-500 mt-2">
                    {calculatedStats.importeCompras > 0 
                      ? `(${formatNumber((calculatedStats.margenTotalConPrevisto / calculatedStats.importeCompras) * 100)}%)`
                      : ''}
                  </p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Resumen por Estado */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Lotes por Estado</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                <div className="text-center p-2 bg-amber-50 rounded-lg">
                  <p className="text-xl font-bold text-amber-700">{stats?.batches_by_status?.documentacion_inicial || 0}</p>
                  <p className="text-[10px] text-amber-600">Doc. Inicial</p>
                </div>
                <div className="text-center p-2 bg-orange-50 rounded-lg">
                  <p className="text-xl font-bold text-orange-700">{stats?.batches_by_status?.en_planta || 0}</p>
                  <p className="text-[10px] text-orange-600">En Planta</p>
                </div>
                <div className="text-center p-2 bg-yellow-50 rounded-lg">
                  <p className="text-xl font-bold text-yellow-700">{stats?.batches_by_status?.en_reconocimiento || 0}</p>
                  <p className="text-[10px] text-yellow-600">Reconocimiento</p>
                </div>
                <div className="text-center p-2 bg-lime-50 rounded-lg">
                  <p className="text-xl font-bold text-lime-700">{stats?.batches_by_status?.listo_para_cargar || 0}</p>
                  <p className="text-[10px] text-lime-600">Listo Cargar</p>
                </div>
                <div className="text-center p-2 bg-sky-50 rounded-lg">
                  <p className="text-xl font-bold text-sky-700">{stats?.batches_by_status?.en_transito || 0}</p>
                  <p className="text-[10px] text-sky-600">En Tránsito</p>
                </div>
                <div className="text-center p-2 bg-cyan-50 rounded-lg">
                  <p className="text-xl font-bold text-cyan-700">{stats?.batches_by_status?.en_destino || 0}</p>
                  <p className="text-[10px] text-cyan-600">En Destino</p>
                </div>
                <div className="text-center p-2 bg-teal-50 rounded-lg">
                  <p className="text-xl font-bold text-teal-700">{stats?.batches_by_status?.entregado_cliente || 0}</p>
                  <p className="text-[10px] text-teal-600">Entregado</p>
                </div>
                <div className="text-center p-2 bg-emerald-50 rounded-lg">
                  <p className="text-xl font-bold text-emerald-700">{stats?.batches_by_status?.completado || 0}</p>
                  <p className="text-[10px] text-emerald-600">Completado</p>
                </div>
                <div className="text-center p-2 bg-gray-50 rounded-lg">
                  <p className="text-xl font-bold text-gray-700">{stats?.batches_by_status?.anulado || 0}</p>
                  <p className="text-[10px] text-gray-600">Anulado</p>
                </div>
                <div className="text-center p-2 bg-red-50 rounded-lg">
                  <p className="text-xl font-bold text-red-700">{stats?.batches_by_status?.rechazado || 0}</p>
                  <p className="text-[10px] text-red-600">Rechazado</p>
                </div>
                <div className="text-center p-2 bg-purple-50 rounded-lg">
                  <p className="text-xl font-bold text-purple-700">{stats?.batches_by_status?.otros || 0}</p>
                  <p className="text-[10px] text-purple-600">Otros</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Consulta de Lotes */}
        <TabsContent value="lotes" className="space-y-4">
          {/* Filtros */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Filter className="w-5 h-5" />
                Filtros
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                <div className="space-y-1">
                  <Label className="text-xs">Fecha Desde</Label>
                  <Input
                    type="date"
                    value={filters.fechaDesde}
                    onChange={(e) => setFilters({ ...filters, fechaDesde: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Fecha Hasta</Label>
                  <Input
                    type="date"
                    value={filters.fechaHasta}
                    onChange={(e) => setFilters({ ...filters, fechaHasta: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Nº Lote</Label>
                  <Input
                    placeholder="Buscar lote..."
                    value={filters.numeroLote}
                    onChange={(e) => setFilters({ ...filters, numeroLote: e.target.value })}
                  />
                </div>
                {/* Filtro Proveedor: solo admin y proveedor, NO clientes */}
                {!isCliente && (
                  <div className="space-y-1">
                    <Label className="text-xs">Proveedor</Label>
                    <Select value={filters.proveedor} onValueChange={(v) => setFilters({ ...filters, proveedor: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {suppliers.map(s => (
                          <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {/* Filtro Cliente: NO visible para proveedores */}
                {!isProveedor && (
                  <div className="space-y-1">
                    <Label className="text-xs">Cliente</Label>
                    <Select value={filters.cliente} onValueChange={(v) => setFilters({ ...filters, cliente: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Todos" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Todos</SelectItem>
                        {clients.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">Producto</Label>
                  <Select value={filters.producto} onValueChange={(v) => setFilters({ ...filters, producto: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {products.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Estado</Label>
                  <Select value={filters.estado} onValueChange={(v) => setFilters({ ...filters, estado: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="pendiente">Pendiente</SelectItem>
                      <SelectItem value="en_transito">En Tránsito</SelectItem>
                      <SelectItem value="recibido">Recibido</SelectItem>
                      <SelectItem value="completado">Completado</SelectItem>
                      <SelectItem value="anulado">Anulado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Contenedor</Label>
                  <Input
                    placeholder="Buscar contenedor..."
                    value={filters.contenedor}
                    onChange={(e) => setFilters({ ...filters, contenedor: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">AWB</Label>
                  <Input
                    placeholder="Buscar AWB..."
                    value={filters.awb}
                    onChange={(e) => setFilters({ ...filters, awb: e.target.value })}
                  />
                </div>
                <div className="flex items-end">
                  <Button variant="outline" onClick={clearFilters} className="w-full">
                    Limpiar Filtros
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Resultados */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex justify-between items-center">
                <CardTitle className="text-lg">
                  Resultados ({filteredBatches.length} lotes)
                </CardTitle>
                <div className="text-sm text-slate-500">
                  Peso Neto Total: <span className="font-bold">{formatNumber(calculatedStats.totalKilosNetos)} Kg</span>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lote</TableHead>
                      <TableHead>Producto</TableHead>
                      <TableHead>Contenedor/AWB</TableHead>
                      <TableHead className="text-right">Peso Neto</TableHead>
                      {/* Precio Coste: solo admin y proveedor, NO clientes */}
                      {!isCliente && <TableHead className="text-right">Precio Coste</TableHead>}
                      {/* Precio Venta: NO visible para proveedores */}
                      {!isProveedor && <TableHead className="text-right">Precio Venta</TableHead>}
                      <TableHead>Estado</TableHead>
                      <TableHead>Fecha</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBatches.slice(0, 50).map((batch) => (
                      <TableRow key={batch.id}>
                        <TableCell className="font-medium">{batch.numero_lote}</TableCell>
                        <TableCell>{batch.producto_nombre || "-"}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {batch.contenedor || batch.awb_aereo || batch.bl_maritimo || "-"}
                        </TableCell>
                        <TableCell className="text-right font-mono">{formatNumber(batch.peso_neto)}</TableCell>
                        {/* Precio Coste: solo admin y proveedor, NO clientes - Convertido de USD a EUR */}
                        {!isCliente && <TableCell className="text-right font-mono">{formatNumber(batch.cotizacion_eur_usd ? (batch.precio_coste || 0) / batch.cotizacion_eur_usd : batch.precio_coste)} €</TableCell>}
                        {/* Precio Venta: NO visible para proveedores */}
                        {!isProveedor && <TableCell className="text-right font-mono">{formatNumber(batch.precio_venta)} €</TableCell>}
                        <TableCell>
                          <span className={`px-2 py-1 rounded text-xs ${
                            batch.estado === 'completado' ? 'bg-emerald-100 text-emerald-700' :
                            batch.estado === 'en_transito' ? 'bg-blue-100 text-blue-700' :
                            batch.estado === 'recibido' ? 'bg-green-100 text-green-700' :
                            batch.estado === 'anulado' ? 'bg-gray-100 text-gray-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}>
                            {batch.estado}
                          </span>
                        </TableCell>
                        <TableCell className="text-xs">{formatDate(batch.created_at)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/batches/${batch.id}/view`)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {filteredBatches.length > 50 && (
                <p className="text-center text-sm text-slate-500 mt-4">
                  Mostrando 50 de {filteredBatches.length} resultados. Utiliza los filtros para refinar la búsqueda.
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Facturación - Filtrado por rol */}
        <TabsContent value="facturacion" className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Facturas de Origen (Compras) - Solo admin y proveedor */}
            {!isCliente && (
              <Card 
                className="cursor-pointer hover:shadow-lg transition-all"
                onClick={() => openDetailView('compras')}
              >
                <CardHeader>
                  <CardTitle className="text-lg text-red-700 flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    Facturas de Origen (Compras)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Contador de facturas */}
                    <div className="grid grid-cols-2 gap-2 pb-2 border-b">
                      <div className="text-center p-2 bg-green-50 rounded">
                        <div className="text-lg font-bold text-green-700">{calculatedStats.facturasOrigenEmitidas}</div>
                        <div className="text-xs text-green-600">Emitidas</div>
                      </div>
                      <div className="text-center p-2 bg-orange-50 rounded">
                        <div className="text-lg font-bold text-orange-700">{calculatedStats.facturasOrigenPendientes}</div>
                        <div className="text-xs text-orange-600">Pte. Emitir</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b">
                      <span className="text-slate-500">Total Lotes:</span>
                      <span className="font-bold">{filteredBatches.length}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b">
                      <span className="text-slate-500">Kilos Netos Totales:</span>
                      <span className="font-bold">{formatNumber(calculatedStats.totalKilosNetos)} Kg</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b">
                      <span className="text-slate-500">Precio Medio Coste:</span>
                      <span className="font-bold">
                        {formatNumber(calculatedStats.totalKilosNetos > 0 
                          ? calculatedStats.importeCompras / calculatedStats.totalKilosNetos 
                          : 0)} €/Kg
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 text-lg">
                      <span className="font-semibold text-red-700">TOTAL COMPRAS:</span>
                      <span className="font-bold text-red-700">{formatCurrency(calculatedStats.importeCompras)}</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-red-500 mt-4">Clic para ver informe detallado →</p>
                </CardContent>
              </Card>
            )}

            {/* Facturas de Cliente (Ventas) - Solo admin y cliente */}
            {!isProveedor && (
              <Card 
                className="cursor-pointer hover:shadow-lg transition-all"
                onClick={() => openDetailView('ventas')}
              >
                <CardHeader>
                  <CardTitle className="text-lg text-green-700 flex items-center gap-2">
                    <Receipt className="w-5 h-5" />
                    Facturas a Cliente (Ventas)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Contador de facturas */}
                    <div className="grid grid-cols-2 gap-2 pb-2 border-b">
                      <div className="text-center p-2 bg-green-50 rounded">
                        <div className="text-lg font-bold text-green-700">{calculatedStats.facturasClienteEmitidas}</div>
                        <div className="text-xs text-green-600">Emitidas</div>
                      </div>
                      <div className="text-center p-2 bg-orange-50 rounded">
                        <div className="text-lg font-bold text-orange-700">{calculatedStats.facturasClientePendientes}</div>
                        <div className="text-xs text-orange-600">Pte. Emitir</div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b">
                      <span className="text-slate-500">Total Lotes:</span>
                      <span className="font-bold">{filteredBatches.length}</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b">
                      <span className="text-slate-500">Kilos Netos Totales:</span>
                      <span className="font-bold">{formatNumber(calculatedStats.totalKilosNetos)} Kg</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b">
                      <span className="text-slate-500">Precio Medio Venta:</span>
                      <span className="font-bold">
                        {formatNumber(calculatedStats.totalKilosNetos > 0 
                          ? calculatedStats.importeVentas / calculatedStats.totalKilosNetos 
                          : 0)} €/Kg
                      </span>
                    </div>
                    {/* Previsto Facturar */}
                    <div className="flex justify-between items-center pb-2 border-b bg-blue-50 p-2 rounded">
                      <span className="text-blue-700 text-sm">Previsto Facturar:</span>
                      <span className="font-bold text-blue-700">{formatCurrency(calculatedStats.previstoFacturar)}</span>
                    </div>
                    <div className="flex justify-between items-center pt-2 text-lg">
                      <span className="font-semibold text-green-700">TOTAL VENTAS:</span>
                      <span className="font-bold text-green-700">{formatCurrency(calculatedStats.importeVentas)}</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-green-500 mt-4">Clic para ver informe detallado →</p>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Margen - Solo admin */}
          {isAdmin && (
            <Card className={calculatedStats.margen >= 0 ? "border-emerald-300" : "border-orange-300"}>
              <CardContent className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-xl font-bold">Margen Bruto Actual</h3>
                      <p className="text-sm text-slate-500">Diferencia entre ventas y compras facturadas</p>
                    </div>
                    <div className="text-right">
                      <p className={`text-3xl font-bold ${calculatedStats.margen >= 0 ? 'text-emerald-600' : 'text-orange-600'}`}>
                        {formatCurrency(calculatedStats.margen)}
                      </p>
                      <p className="text-sm text-slate-500">
                        {calculatedStats.importeCompras > 0 
                          ? `(${formatNumber((calculatedStats.margen / calculatedStats.importeCompras) * 100)}%)`
                          : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-between items-center bg-blue-50 p-4 rounded">
                    <div>
                      <h3 className="text-xl font-bold text-blue-800">Margen Total (con Previsto)</h3>
                      <p className="text-sm text-blue-600">Incluye previsión de facturación pendiente</p>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-bold text-blue-700">
                        {formatCurrency(calculatedStats.margenTotalConPrevisto)}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
